import {
  User,
  Challenge,
  ExerciseLog,
  UserStats,
  ApiResponse,
  AuthResponse,
  ChallengeSchema,
  ExerciseLogSchema,
  UserSchema,
} from '@/types';
import { CONFIG, API_ENDPOINTS, ERROR_MESSAGES } from '@/constants';
import { generateId, generateChallengeId, getExerciseForChallenge } from './utils';

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = CONFIG.apiUrl;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, defaultOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new ApiError(data.error || ERROR_MESSAGES.serverError, response.status, data.code);
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(ERROR_MESSAGES.networkError, 0, 'NETWORK_ERROR');
    }
  }

  // Authentication
  async authenticate(username: string): Promise<User> {
    const response = await this.request<AuthResponse>(API_ENDPOINTS.auth, {
      method: 'POST',
      body: JSON.stringify({ username }),
    });

    const user = UserSchema.parse(response.data?.user);
    return user;
  }

  async getUserById(userId: string): Promise<User | null> {
    try {
      const response = await this.request<{user: User}>(`${API_ENDPOINTS.auth}/${userId}`);
      
      // The API returns {success: true, data: {user: {...}}, timestamp: ...}
      if (!response.data || !response.data.user) {
        return null;
      }
      
      const user = UserSchema.parse(response.data.user);
      return user;
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Challenge operations
  async createChallenge(
    challengeData: Omit<Challenge, 'id' | 'createdAt' | 'updatedAt'>
  ): Promise<Challenge> {
    const challenge: Challenge = {
      ...challengeData,
      id: generateChallengeId(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    const validatedChallenge = ChallengeSchema.parse(challenge);

    const response = await this.request<Challenge>(API_ENDPOINTS.challenges, {
      method: 'POST',
      body: JSON.stringify(validatedChallenge),
    });

    return ChallengeSchema.parse(response.data);
  }

  async joinChallenge(challengeId: string, userId: string): Promise<Challenge> {
    
    const response = await this.request<Challenge>(`${API_ENDPOINTS.challenges}/${challengeId}/join`, {
      method: 'POST',
      body: JSON.stringify({ userId }),
    });

    return ChallengeSchema.parse(response.data);
  }

  async getChallenge(challengeId: string): Promise<Challenge | null> {
    try {
      const response = await this.request<Challenge>(`${API_ENDPOINTS.challenges}/${challengeId}`);
      return ChallengeSchema.parse(response.data);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getUserChallenges(userId: string): Promise<Challenge[]> {
    try {
      const response = await this.request<Challenge[]>(`${API_ENDPOINTS.challenges}?userId=${userId}`);
      return (response.data || []).map(c => ChallengeSchema.parse(c));
    } catch (error) {
      console.error('Failed to get user challenges:', error);
      return [];
    }
  }

  // Exercise logging
  async logExercise(
    exerciseData: Omit<ExerciseLog, 'id'>,
    challenge?: Challenge
  ): Promise<ExerciseLog> {
    const exerciseLog: ExerciseLog = {
      ...exerciseData,
      id: generateId(),
    };

    // Add exercise type and unit if challenge is provided
    if (challenge) {
      const exercise = getExerciseForChallenge(challenge);
      if (challenge.customExercise) {
        exerciseLog.exerciseType = challenge.exerciseType;
        exerciseLog.exerciseUnit = exercise.unit;
      } else {
        exerciseLog.exerciseType = (exercise as any).id;
        exerciseLog.exerciseUnit = exercise.unit;
      }
    }

    const validatedLog = ExerciseLogSchema.parse(exerciseLog);

    const response = await this.request<ExerciseLog>(API_ENDPOINTS.activity, {
      method: 'POST',
      body: JSON.stringify(validatedLog),
    });

    return ExerciseLogSchema.parse(response.data);
  }

  async getExerciseLogsForChallenge(challengeId: string): Promise<ExerciseLog[]> {
    try {
      const response = await this.request<ExerciseLog[]>(
        `${API_ENDPOINTS.activity}?challengeId=${challengeId}`
      );
      return (response.data || []).map(l => ExerciseLogSchema.parse(l));
    } catch (error) {
      console.error('Failed to get exercise logs:', error);
      return [];
    }
  }

  // Participants
  async getChallengeParticipants(challengeId: string): Promise<any[]> {
    try {
      const response = await this.request<any[]>(`${API_ENDPOINTS.participants}/${challengeId}`);
      return response.data || [];
    } catch (error) {
      console.error('Failed to get participants:', error);
      return [];
    }
  }

  // User stats
  async getUserStats(userId: string): Promise<UserStats | null> {
    try {
      const response = await this.request<UserStats>(`/api/users/${userId}/stats`);
      return response.data || null;
    } catch (error) {
      console.error('Failed to get user stats:', error);
      return null;
    }
  }

  // Wallet pass
  async getWalletPass(userId: string): Promise<ArrayBuffer> {
    const response = await fetch(`${this.baseUrl}/api/users/${userId}/wallet`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new ApiError('Failed to generate wallet pass', response.status);
    }

    return response.arrayBuffer();
  }
}

export const apiClient = new ApiClient();